import React from 'react';
import { CreditCard, DollarSign, Percent, TrendingDown } from 'lucide-react';
import { usePanelPaymentFunnel } from '../../lib/api';
import { DateRange } from '../../lib/panelTypes';
import StatCard from './StatCard';

const dkk = (n: number) =>
  new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(n || 0);
const num = (n: number) => (n || 0).toLocaleString();

const STEP_LABELS: Record<string, string> = {
  started: 'Journey started',
  duration_selected: 'Duration selected',
  checkout_viewed: 'Checkout viewed',
  submit_started: 'Submit started',
  checkout_created: 'Checkout created',
  completed: 'Completed',
};

const PaymentFunnelTab: React.FC<{ range: DateRange }> = ({ range }) => {
  const { data, isLoading } = usePanelPaymentFunnel(range);
  const steps = data?.steps ?? [];
  const started = steps.find((s) => s.key === 'started')?.count ?? 0;
  const completed = steps.find((s) => s.key === 'completed')?.count ?? 0;
  const conversion = started > 0 ? (completed / started) * 100 : 0;
  const hasData = started > 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Journeys started" value={num(started)} icon={CreditCard} />
        <StatCard label="Completed" value={num(completed)} icon={CreditCard} />
        <StatCard label="Conversion" value={`${conversion.toFixed(1)}%`} icon={Percent} hint="completed / started" />
        <StatCard label="Revenue" value={dkk(data?.revenue ?? 0)} icon={DollarSign} hint={`AOV ${dkk(data?.avgOrderValue ?? 0)}`} />
      </div>

      {!hasData ? (
        <div className="bg-white border rounded-lg p-8 text-center">
          <TrendingDown className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-600 font-medium">No payment journeys in this range yet</p>
          <p className="text-sm text-gray-400 mt-1">
            The payment funnel fills once the updated app (with backend analytics) is live in the stores.
          </p>
        </div>
      ) : (
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Checkout funnel</h3>
          <div className="space-y-3">
            {steps.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{STEP_LABELS[s.key] || s.event_name}</span>
                  <span className="text-gray-500">
                    {num(s.count)} · {s.pctOfStart.toFixed(0)}%
                    {s.key !== 'started' && <span className="text-gray-400"> ({s.pctOfPrev.toFixed(0)}% of prev)</span>}
                  </span>
                </div>
                <div className="h-6 bg-gray-100 rounded overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded" style={{ width: `${Math.max(2, s.pctOfStart)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasData && (data?.dropoffReasons?.length ?? 0) > 0 && (
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Drop-off reasons</h3>
          <div className="space-y-2">
            {data!.dropoffReasons.map((d) => (
              <div key={d.reason} className="flex justify-between text-sm">
                <span className="text-gray-700 capitalize">{d.reason.replace(/_/g, ' ')}</span>
                <span className="text-gray-500">{num(d.count)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasData && (data?.flowTypes?.length ?? 0) > 0 && (
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Flow type</h3>
          <div className="flex gap-8">
            {data!.flowTypes.map((f) => (
              <div key={f.flow_type}>
                <div className="text-2xl font-bold text-gray-900">{num(f.count)}</div>
                <div className="text-sm text-gray-500 capitalize">{f.flow_type.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {isLoading && <p className="text-xs text-gray-400">Loading…</p>}
    </div>
  );
};

export default PaymentFunnelTab;
